import { useEffect, useState, type ReactNode } from 'react'

import { ProblemAlert } from '@/components/error-surface'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { ApiError } from '@/lib/problem-details'

/**
 * A confirmation that cannot be given without saying why.
 *
 * The shared confirmation dialog covers the case where agreement is enough. Where the API records
 * a reason — a rejected KYC file, a status change — the reason is part of the decision and the
 * button stays closed until it is written. It is also what the customer is told and what the
 * examiner reads, so it is captured while the decision is being made rather than reconstructed.
 */

/** Long enough to be a reason. "No" and "bad" are not reasons anyone can act on later. */
const MINIMUM_REASON_LENGTH = 5

export interface ReasonDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: ReactNode
  /** Label above the reason field, e.g. "Reason for rejection". */
  reasonLabel: string
  reasonDescription?: string
  /** Extra detail rendered above the reason field. */
  children?: ReactNode
  confirmLabel: string
  tone?: 'default' | 'danger'
  isPending?: boolean
  error?: ApiError | null
  onConfirm: (reason: string) => void
}

export function ReasonDialog({
  open,
  onOpenChange,
  title,
  description,
  reasonLabel,
  reasonDescription,
  children,
  confirmLabel,
  tone = 'default',
  isPending = false,
  error = null,
  onConfirm,
}: ReasonDialogProps) {
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (!open) {
      setReason('')
    }
  }, [open])

  const trimmed = reason.trim()
  const satisfied = trimmed.length >= MINIMUM_REASON_LENGTH

  const confirm = () => {
    if (satisfied && !isPending) {
      onConfirm(trimmed)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3">
          {children}

          <div className="space-y-1.5">
            <Label htmlFor="reason-dialog-reason">{reasonLabel}</Label>
            <Input
              id="reason-dialog-reason"
              value={reason}
              autoFocus
              autoComplete="off"
              onChange={(event) => setReason(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  confirm()
                }
              }}
              aria-describedby={reasonDescription ? 'reason-dialog-description' : undefined}
            />
            {reasonDescription ? (
              <p id="reason-dialog-description" className="text-xs text-muted">
                {reasonDescription}
              </p>
            ) : null}
          </div>

          {error ? <ProblemAlert error={error} /> : null}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            onClick={confirm}
            disabled={isPending || !satisfied}
          >
            {isPending ? 'Working…' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
